import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
    DollarSign, TrendingUp, TrendingDown, Plus, Edit, Trash2,
    Receipt, CreditCard, Building, Users, Shield, Medal, Trophy,
    Utensils, Car, Gift, BarChart3, Calendar, Search, Filter,
    ChevronDown, ChevronRight, Wallet, PiggyBank, FileText,
    Download, Upload, FileSpreadsheet, File
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Revenue categories with icons
const REVENUE_CATEGORIES = [
    { id: 'registration_fee', name: 'Registration Fee', icon: Receipt, color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' },
    { id: 'sponsorship', name: 'Sponsorship Revenue', icon: Building, color: 'text-green-400', bgColor: 'bg-green-500/20' },
    { id: 'branding', name: 'Branding Revenue', icon: FileText, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
    { id: 'stalls_renting', name: 'Stalls Renting', icon: Building, color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
    { id: 'vouchers_gifts', name: 'Vouchers and Gifts', icon: Gift, color: 'text-pink-400', bgColor: 'bg-pink-500/20' },
    { id: 'others', name: 'Others', icon: Wallet, color: 'text-gray-400', bgColor: 'bg-gray-500/20' }
];

// Expense categories with icons
const EXPENSE_CATEGORIES = [
    { id: 'officials_referees_volunteers', name: 'Officials, Referees & Volunteers', icon: Users, color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' },
    { id: 'vendors', name: 'Vendors', icon: Building, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
    { id: 'venue_rent', name: 'Venue Rent', icon: Building, color: 'text-indigo-400', bgColor: 'bg-indigo-500/20' },
    { id: 'medical_team', name: 'Medical Team', icon: Shield, color: 'text-red-400', bgColor: 'bg-red-500/20' },
    { id: 'security', name: 'Security', icon: Shield, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
    { id: 'medals', name: 'Medals', icon: Medal, color: 'text-amber-400', bgColor: 'bg-amber-500/20' },
    { id: 'trophies', name: 'Trophies', icon: Trophy, color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
    { id: 'prize_money', name: 'Prize Money / Vouchers', icon: DollarSign, color: 'text-green-400', bgColor: 'bg-green-500/20' },
    { id: 'food', name: 'Food Expenses', icon: Utensils, color: 'text-pink-400', bgColor: 'bg-pink-500/20' },
    { id: 'transport', name: 'Transport Expenses', icon: Car, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
    { id: 'others', name: 'Others', icon: Wallet, color: 'text-gray-400', bgColor: 'bg-gray-500/20' }
];

const PAYMENT_METHODS = ['Cash', 'M-Pesa', 'Bank Transfer', 'Card Payment', 'Cheque', 'Online Payment'];

const AccountingTab = ({ tournaments, token }) => {
    const [activeSection, setActiveSection] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [revenues, setRevenues] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [tournamentSummaries, setTournamentSummaries] = useState(null);
    const [schoolsWithWaiver, setSchoolsWithWaiver] = useState([]);
    const [departmentData, setDepartmentData] = useState(null);
    
    // Filters
    const [selectedTournament, setSelectedTournament] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    
    // Dialog states
    const [showRevenueDialog, setShowRevenueDialog] = useState(false);
    const [showExpenseDialog, setShowExpenseDialog] = useState(false);
    const [editingRevenue, setEditingRevenue] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [importType, setImportType] = useState('revenue');
    const [importing, setImporting] = useState(false);
    
    // File input refs
    const revenueFileRef = useRef(null);
    const expenseFileRef = useRef(null);
    
    // Form states
    const [revenueForm, setRevenueForm] = useState({
        category: '',
        description: '',
        amount: '',
        currency: 'KES',
        tournament_id: '',
        payment_method: '',
        payer_name: '',
        reference_number: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });
    
    const [expenseForm, setExpenseForm] = useState({
        category: '',
        description: '',
        amount: '',
        currency: 'KES',
        tournament_id: '',
        payment_method: '',
        payee_name: '',
        reference_number: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        loadData();
    }, [selectedTournament]);

    const loadData = async () => {
        setLoading(true);
        try {
            const params = selectedTournament !== 'all' ? { tournament_id: selectedTournament } : {};
            
            // Fetch main data
            const [summaryRes, revenuesRes, expensesRes, schoolsRes] = await Promise.all([
                api.get('/accounting/summary', { params }),
                api.get('/accounting/revenue', { params: { ...params, limit: 100 } }),
                api.get('/accounting/expenses', { params: { ...params, limit: 100 } }),
                api.get('/school-rankings').catch(() => ({ data: [] }))
            ]);
            
            setSummary(summaryRes.data);
            setRevenues(revenuesRes.data.revenues || []);
            setExpenses(expensesRes.data.expenses || []);
            
            // Filter schools with waivers for Financial Aid report
            const schools = schoolsRes.data || [];
            const waiverSchools = schools.filter(s => s.fee_waiver_type && s.fee_waiver_type !== 'none');
            setSchoolsWithWaiver(waiverSchools);
            
            // If viewing all tournaments, also fetch tournament breakdown and department data
            if (selectedTournament === 'all') {
                try {
                    const [tournamentSummaryRes, deptRes] = await Promise.all([
                        api.get('/accounting/summary/by-tournament'),
                        api.get('/accounting/by-department'),
                    ]);
                    setTournamentSummaries(tournamentSummaryRes.data);
                    setDepartmentData(deptRes.data);
                } catch (err) {
                    console.error('Failed to load breakdowns:', err);
                    setTournamentSummaries(null);
                    setDepartmentData(null);
                }
            } else {
                setTournamentSummaries(null);
                setDepartmentData(null);
            }
        } catch (error) {
            console.error('Failed to load accounting data:', error);
            // Set empty data on error
            setSummary({ total_revenue: 0, total_expenses: 0, net_balance: 0, revenue_by_category: [], expense_by_category: [] });
            setRevenues([]);
            setExpenses([]);
            setTournamentSummaries(null);
            setSchoolsWithWaiver([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRevenue = async () => {
        if (!revenueForm.category || !revenueForm.description || !revenueForm.amount) {
            toast.error('Please fill in category, description and amount');
            return;
        }
        
        try {
            const data = {
                ...revenueForm,
                amount: parseFloat(revenueForm.amount),
                tournament_id: revenueForm.tournament_id || null
            };
            
            if (editingRevenue) {
                await api.put(`/accounting/revenue/${editingRevenue.id}`, data);
                toast.success('Revenue entry updated');
            } else {
                await api.post('/accounting/revenue', data);
                toast.success('Revenue entry created');
            }
            
            setShowRevenueDialog(false);
            resetRevenueForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save revenue entry');
        }
    };

    const handleSaveExpense = async () => {
        if (!expenseForm.category || !expenseForm.description || !expenseForm.amount) {
            toast.error('Please fill in category, description and amount');
            return;
        }
        
        try {
            const data = {
                ...expenseForm,
                amount: parseFloat(expenseForm.amount),
                tournament_id: expenseForm.tournament_id || null
            };
            
            if (editingExpense) {
                await api.put(`/accounting/expenses/${editingExpense.id}`, data);
                toast.success('Expense entry updated');
            } else {
                await api.post('/accounting/expenses', data);
                toast.success('Expense entry created');
            }
            
            setShowExpenseDialog(false);
            resetExpenseForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save expense entry');
        }
    };

    const handleDeleteRevenue = async (id) => {
        if (!window.confirm('Are you sure you want to delete this revenue entry?')) return;
        
        try {
            await api.delete(`/accounting/revenue/${id}`);
            toast.success('Revenue entry deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete revenue entry');
        }
    };

    const handleDeleteExpense = async (id) => {
        if (!window.confirm('Are you sure you want to delete this expense entry?')) return;
        
        try {
            await api.delete(`/accounting/expenses/${id}`);
            toast.success('Expense entry deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete expense entry');
        }
    };

    const handleEditRevenue = (revenue) => {
        setEditingRevenue(revenue);
        setRevenueForm({
            category: revenue.category || '',
            description: revenue.description || '',
            amount: revenue.amount?.toString() || '',
            currency: revenue.currency || 'KES',
            tournament_id: revenue.tournament_id || '',
            payment_method: revenue.payment_method || '',
            payer_name: revenue.payer_name || '',
            reference_number: revenue.reference_number || '',
            date: revenue.date || new Date().toISOString().split('T')[0],
            notes: revenue.notes || ''
        });
        setShowRevenueDialog(true);
    };

    const handleEditExpense = (expense) => {
        setEditingExpense(expense);
        setExpenseForm({
            category: expense.category || '',
            description: expense.description || '',
            amount: expense.amount?.toString() || '',
            currency: expense.currency || 'KES',
            tournament_id: expense.tournament_id || '',
            payment_method: expense.payment_method || '',
            payee_name: expense.payee_name || '',
            reference_number: expense.reference_number || '',
            date: expense.date || new Date().toISOString().split('T')[0],
            notes: expense.notes || ''
        });
        setShowExpenseDialog(true);
    };

    const resetRevenueForm = () => {
        setEditingRevenue(null);
        setRevenueForm({
            category: '',
            description: '',
            amount: '',
            currency: 'KES',
            tournament_id: '',
            payment_method: '',
            payer_name: '',
            reference_number: '',
            date: new Date().toISOString().split('T')[0],
            notes: ''
        });
    };

    const resetExpenseForm = () => {
        setEditingExpense(null);
        setExpenseForm({
            category: '',
            description: '',
            amount: '',
            currency: 'KES',
            tournament_id: '',
            payment_method: '',
            payee_name: '',
            reference_number: '',
            date: new Date().toISOString().split('T')[0],
            notes: ''
        });
    };

    const formatCurrency = (amount, currency = 'KES') => {
        return `${currency} ${(amount || 0).toLocaleString()}`;
    };

    const getCategoryInfo = (categoryId, type) => {
        const categories = type === 'revenue' ? REVENUE_CATEGORIES : EXPENSE_CATEGORIES;
        return categories.find(c => c.id === categoryId) || { name: categoryId, icon: Wallet, color: 'text-gray-400', bgColor: 'bg-gray-500/20' };
    };

    // Export handlers
    const handleExportRevenue = async (format = 'csv') => {
        try {
            const params = selectedTournament !== 'all' ? `?format=${format}&tournament_id=${selectedTournament}` : `?format=${format}`;
            const response = await api.get(`/accounting/export/revenue${params}`, { responseType: 'blob' });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `revenue_export.${format === 'excel' ? 'xlsx' : 'csv'}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success(`Revenue exported to ${format.toUpperCase()}`);
        } catch (error) {
            toast.error('Failed to export revenue');
        }
    };

    const handleExportExpenses = async (format = 'csv') => {
        try {
            const params = selectedTournament !== 'all' ? `?format=${format}&tournament_id=${selectedTournament}` : `?format=${format}`;
            const response = await api.get(`/accounting/export/expenses${params}`, { responseType: 'blob' });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `expenses_export.${format === 'excel' ? 'xlsx' : 'csv'}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success(`Expenses exported to ${format.toUpperCase()}`);
        } catch (error) {
            toast.error('Failed to export expenses');
        }
    };

    const handleExportAll = async () => {
        try {
            const params = selectedTournament !== 'all' ? `?tournament_id=${selectedTournament}` : '';
            const response = await api.get(`/accounting/export/all${params}`, { responseType: 'blob' });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'accounting_full_export.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Full accounting data exported to Excel');
        } catch (error) {
            toast.error('Failed to export accounting data');
        }
    };

    const handleDownloadTemplate = async (type, format = 'csv') => {
        try {
            const response = await api.get(`/accounting/template/${type}?format=${format}`, { responseType: 'blob' });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${type}_import_template.${format === 'excel' ? 'xlsx' : 'csv'}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} template downloaded`);
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const handleImportFile = async (event, type) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
            toast.error('Please upload a CSV or Excel file');
            return;
        }
        
        setImporting(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await api.post(`/accounting/import/${type}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const result = response.data;
            
            if (result.imported > 0) {
                toast.success(`Successfully imported ${result.imported} ${type} entries`);
                loadData();
            }
            
            if (result.error_count > 0) {
                toast.warning(`${result.error_count} rows had errors`);
            }
            
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to import ${type}`);
        } finally {
            setImporting(false);
            // Reset file input
            if (type === 'revenue' && revenueFileRef.current) {
                revenueFileRef.current.value = '';
            } else if (type === 'expenses' && expenseFileRef.current) {
                expenseFileRef.current.value = '';
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Hidden file inputs for import */}
            <input
                type="file"
                ref={revenueFileRef}
                onChange={(e) => handleImportFile(e, 'revenue')}
                accept=".csv,.xlsx,.xls"
                className="hidden"
            />
            <input
                type="file"
                ref={expenseFileRef}
                onChange={(e) => handleImportFile(e, 'expenses')}
                accept=".csv,.xlsx,.xls"
                className="hidden"
            />

            {/* Header with filters */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                        <PiggyBank className="w-6 h-6 text-primary" />
                        Accounting
                        {selectedTournament !== 'all' && (
                            <span className="text-cyan-400 text-sm font-normal ml-2">
                                ({tournaments?.find(t => t.id === selectedTournament)?.name || 'Tournament'})
                            </span>
                        )}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {selectedTournament === 'all' 
                            ? 'View all tournament finances or select a specific tournament'
                            : 'Managing revenue and expenses for selected tournament'}
                    </p>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={selectedTournament} onValueChange={(val) => {
                        setSelectedTournament(val);
                        // Reset to overview when switching tournaments
                        if (activeSection === 'tournament-breakdown' && val !== 'all') {
                            setActiveSection('overview');
                        }
                        if (activeSection === 'department-breakdown' && val !== 'all') {
                            setActiveSection('overview');
                        }
                    }}>
                        <SelectTrigger className="w-[220px] bg-[#0a1628] border-white/10" data-testid="tournament-selector">
                            <SelectValue placeholder="All Tournaments Summary" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10">
                            <SelectItem value="all">All Tournaments Summary</SelectItem>
                            {tournaments?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    <Button
                        onClick={() => { resetRevenueForm(); setShowRevenueDialog(true); }}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="add-revenue-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Revenue
                    </Button>
                    
                    <Button
                        onClick={() => { resetExpenseForm(); setShowExpenseDialog(true); }}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="add-expense-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Expense
                    </Button>
                </div>
            </div>

            {/* Export/Import Actions Bar */}
            <div className="bg-[#0d2847] border border-white/5 p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground mr-2">Export:</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportRevenue('csv')}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        data-testid="export-revenue-csv-btn"
                    >
                        <Download className="w-4 h-4 mr-1" />
                        Revenue CSV
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportExpenses('csv')}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid="export-expenses-csv-btn"
                    >
                        <Download className="w-4 h-4 mr-1" />
                        Expenses CSV
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportAll}
                        className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                        data-testid="export-all-btn"
                    >
                        <FileSpreadsheet className="w-4 h-4 mr-1" />
                        Export All (Excel)
                    </Button>
                </div>
                
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground mr-2">Import:</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revenueFileRef.current?.click()}
                        disabled={importing}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        data-testid="import-revenue-btn"
                    >
                        <Upload className="w-4 h-4 mr-1" />
                        Revenue
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => expenseFileRef.current?.click()}
                        disabled={importing}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid="import-expenses-btn"
                    >
                        <Upload className="w-4 h-4 mr-1" />
                        Expenses
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowImportDialog(true)}
                        className="text-muted-foreground hover:text-white"
                    >
                        <File className="w-4 h-4 mr-1" />
                        Templates
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Total Revenue */}
                <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 border border-green-500/30 p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-green-400" />
                        </div>
                        <span className="text-xs tracking-widest uppercase text-green-400">Total Revenue</span>
                    </div>
                    <p className="stat-display text-3xl text-green-400" data-testid="total-revenue">
                        {formatCurrency(summary?.total_revenue)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {summary?.revenue_by_category?.length || 0} categories
                    </p>
                </div>

                {/* Total Expenses */}
                <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 border border-red-500/30 p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-red-500/20 flex items-center justify-center">
                            <TrendingDown className="w-5 h-5 text-red-400" />
                        </div>
                        <span className="text-xs tracking-widest uppercase text-red-400">Total Expenses</span>
                    </div>
                    <p className="stat-display text-3xl text-red-400" data-testid="total-expenses">
                        {formatCurrency(summary?.total_expenses)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {summary?.expense_by_category?.length || 0} categories
                    </p>
                </div>

                {/* Net Balance */}
                <div className={`bg-gradient-to-br ${(summary?.net_balance || 0) >= 0 
                    ? 'from-cyan-900/50 to-cyan-800/30 border-cyan-500/30' 
                    : 'from-orange-900/50 to-orange-800/30 border-orange-500/30'} border p-6`}>
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 ${(summary?.net_balance || 0) >= 0 ? 'bg-cyan-500/20' : 'bg-orange-500/20'} flex items-center justify-center`}>
                            <DollarSign className={`w-5 h-5 ${(summary?.net_balance || 0) >= 0 ? 'text-cyan-400' : 'text-orange-400'}`} />
                        </div>
                        <span className={`text-xs tracking-widest uppercase ${(summary?.net_balance || 0) >= 0 ? 'text-cyan-400' : 'text-orange-400'}`}>
                            Net Balance
                        </span>
                    </div>
                    <p className={`stat-display text-3xl ${(summary?.net_balance || 0) >= 0 ? 'text-cyan-400' : 'text-orange-400'}`} data-testid="net-balance">
                        {formatCurrency(summary?.net_balance)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {(summary?.net_balance || 0) >= 0 ? 'Profit' : 'Loss'}
                    </p>
                </div>
            </div>

            {/* Section Tabs */}
            <div className="flex gap-2 border-b border-white/10 pb-2 flex-wrap">
                <Button
                    variant={activeSection === 'overview' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveSection('overview')}
                    className={activeSection === 'overview' ? 'bg-primary' : ''}
                >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Overview
                </Button>
                {/* Tournament Breakdown tab - only show when viewing all tournaments */}
                {selectedTournament === 'all' && (
                    <Button
                        variant={activeSection === 'tournament-breakdown' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveSection('tournament-breakdown')}
                        className={activeSection === 'tournament-breakdown' ? 'bg-cyan-600' : ''}
                        data-testid="tournament-breakdown-tab"
                    >
                        <Trophy className="w-4 h-4 mr-2" />
                        By Tournament
                    </Button>
                )}
                {selectedTournament === 'all' && (
                    <Button
                        variant={activeSection === 'department-breakdown' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveSection('department-breakdown')}
                        className={activeSection === 'department-breakdown' ? 'bg-amber-600' : ''}
                        data-testid="department-breakdown-tab"
                    >
                        <Building className="w-4 h-4 mr-2" />
                        By Department
                    </Button>
                )}
                <Button
                    variant={activeSection === 'revenue' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveSection('revenue')}
                    className={activeSection === 'revenue' ? 'bg-green-600' : ''}
                >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Revenue
                </Button>
                <Button
                    variant={activeSection === 'expenses' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveSection('expenses')}
                    className={activeSection === 'expenses' ? 'bg-red-600' : ''}
                >
                    <TrendingDown className="w-4 h-4 mr-2" />
                    Expenses
                </Button>
                <Button
                    variant={activeSection === 'financial-aid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveSection('financial-aid')}
                    className={activeSection === 'financial-aid' ? 'bg-purple-600' : ''}
                    data-testid="financial-aid-tab"
                >
                    <Gift className="w-4 h-4 mr-2" />
                    Financial Aid
                    {schoolsWithWaiver.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-purple-500/30 rounded-full text-xs">
                            {schoolsWithWaiver.length}
                        </span>
                    )}
                </Button>
            </div>

            {/* Overview Section */}
            {activeSection === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Revenue Breakdown */}
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-400" />
                            Revenue Breakdown
                        </h3>
                        <div className="space-y-3">
                            {summary?.revenue_by_category?.length > 0 ? (
                                summary.revenue_by_category.map((item) => {
                                    const catInfo = getCategoryInfo(item.category_id, 'revenue');
                                    const Icon = catInfo.icon;
                                    const percentage = summary.total_revenue > 0 
                                        ? ((item.total / summary.total_revenue) * 100).toFixed(1) 
                                        : 0;
                                    
                                    return (
                                        <div key={item.category_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 ${catInfo.bgColor} flex items-center justify-center`}>
                                                    <Icon className={`w-4 h-4 ${catInfo.color}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{item.category_name}</p>
                                                    <p className="text-xs text-muted-foreground">{item.count} entries</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-bold text-green-400">{formatCurrency(item.total)}</p>
                                                <p className="text-xs text-muted-foreground">{percentage}%</p>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-muted-foreground text-center py-4">No revenue entries yet</p>
                            )}
                        </div>
                    </div>

                    {/* Expense Breakdown */}
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-red-400" />
                            Expense Breakdown
                        </h3>
                        <div className="space-y-3">
                            {summary?.expense_by_category?.length > 0 ? (
                                summary.expense_by_category.map((item) => {
                                    const catInfo = getCategoryInfo(item.category_id, 'expense');
                                    const Icon = catInfo.icon;
                                    const percentage = summary.total_expenses > 0 
                                        ? ((item.total / summary.total_expenses) * 100).toFixed(1) 
                                        : 0;
                                    
                                    return (
                                        <div key={item.category_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 ${catInfo.bgColor} flex items-center justify-center`}>
                                                    <Icon className={`w-4 h-4 ${catInfo.color}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{item.category_name}</p>
                                                    <p className="text-xs text-muted-foreground">{item.count} entries</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-bold text-red-400">{formatCurrency(item.total)}</p>
                                                <p className="text-xs text-muted-foreground">{percentage}%</p>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-muted-foreground text-center py-4">No expense entries yet</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Tournament Breakdown Section - Only when "All Tournaments" is selected */}
            {activeSection === 'tournament-breakdown' && selectedTournament === 'all' && (
                <div className="space-y-6">
                    {/* Grand Totals */}
                    <div className="bg-[#0d2847] border border-cyan-500/30 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-cyan-400" />
                            All Tournaments Financial Summary
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Tournaments</p>
                                <p className="text-2xl font-bold text-cyan-400" data-testid="total-tournaments">
                                    {tournamentSummaries?.tournament_count || 0}
                                </p>
                            </div>
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Total Revenue</p>
                                <p className="text-2xl font-bold text-green-400" data-testid="grand-total-revenue">
                                    {formatCurrency(tournamentSummaries?.grand_total_revenue)}
                                </p>
                            </div>
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Total Expenses</p>
                                <p className="text-2xl font-bold text-red-400" data-testid="grand-total-expenses">
                                    {formatCurrency(tournamentSummaries?.grand_total_expenses)}
                                </p>
                            </div>
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Net Balance</p>
                                <p className={`text-2xl font-bold ${(tournamentSummaries?.grand_net_balance || 0) >= 0 ? 'text-cyan-400' : 'text-orange-400'}`} data-testid="grand-net-balance">
                                    {formatCurrency(tournamentSummaries?.grand_net_balance)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tournament Breakdown Table */}
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Tournament-wise Breakdown
                        </h3>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Tournament</th>
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Sport</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Revenue</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Expenses</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Net Balance</th>
                                        <th className="text-center py-3 px-2 text-xs text-muted-foreground uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tournamentSummaries?.tournament_summaries?.length > 0 ? (
                                        tournamentSummaries.tournament_summaries.map((ts, idx) => (
                                            <tr key={ts.tournament_id || `unassigned-${idx}`} className="border-b border-white/5 hover:bg-white/5">
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-8 h-8 ${ts.tournament_id ? 'bg-cyan-500/20' : 'bg-gray-500/20'} flex items-center justify-center`}>
                                                            <Trophy className={`w-4 h-4 ${ts.tournament_id ? 'text-cyan-400' : 'text-gray-400'}`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium">{ts.tournament_name}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {ts.revenue_count} revenues, {ts.expense_count} expenses
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-sm text-muted-foreground">{ts.sport || '-'}</td>
                                                <td className="py-3 px-2 text-sm text-right font-bold text-green-400">
                                                    {formatCurrency(ts.total_revenue)}
                                                </td>
                                                <td className="py-3 px-2 text-sm text-right font-bold text-red-400">
                                                    {formatCurrency(ts.total_expenses)}
                                                </td>
                                                <td className={`py-3 px-2 text-sm text-right font-bold ${ts.net_balance >= 0 ? 'text-cyan-400' : 'text-orange-400'}`}>
                                                    {formatCurrency(ts.net_balance)}
                                                </td>
                                                <td className="py-3 px-2 text-center">
                                                    {ts.tournament_id && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedTournament(ts.tournament_id);
                                                                setActiveSection('overview');
                                                            }}
                                                            className="text-cyan-400 hover:text-cyan-300"
                                                            data-testid={`view-tournament-${ts.tournament_id}`}
                                                        >
                                                            <ChevronRight className="w-4 h-4 mr-1" />
                                                            View Details
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="py-8 text-center text-muted-foreground">
                                                No tournament financial data available yet. Add revenue or expenses linked to tournaments.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Department Breakdown Section */}
            {activeSection === 'department-breakdown' && selectedTournament === 'all' && departmentData && (
                <div className="space-y-6">
                    {/* Department Summary Cards */}
                    <div className="bg-[#0d2847] border border-amber-500/30 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <Building className="w-5 h-5 text-amber-400" />
                            Department Financial Summary
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Departments</p>
                                <p className="text-2xl font-bold text-amber-400" data-testid="total-departments">
                                    {departmentData.total_departments}
                                </p>
                            </div>
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Total Revenue</p>
                                <p className="text-2xl font-bold text-green-400" data-testid="dept-total-revenue">
                                    {formatCurrency(departmentData.total_revenue)}
                                </p>
                            </div>
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Total Expenses</p>
                                <p className="text-2xl font-bold text-red-400" data-testid="dept-total-expenses">
                                    {formatCurrency(departmentData.total_expenses)}
                                </p>
                            </div>
                            <div className="bg-[#0a1628] p-4 border border-white/5">
                                <p className="text-xs text-muted-foreground uppercase mb-1">Net Balance</p>
                                <p className={`text-2xl font-bold ${departmentData.net_balance >= 0 ? 'text-cyan-400' : 'text-orange-400'}`} data-testid="dept-net-balance">
                                    {formatCurrency(departmentData.net_balance)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Department Breakdown Table */}
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Department-wise Breakdown
                        </h3>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Department</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Revenue</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Expenses</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Net Balance</th>
                                        <th className="text-center py-3 px-2 text-xs text-muted-foreground uppercase">Entries</th>
                                        <th className="text-center py-3 px-2 text-xs text-muted-foreground uppercase">Share %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {departmentData.departments.map((dept, idx) => {
                                        const sharePercent = departmentData.total_revenue > 0
                                            ? ((dept.total_revenue / departmentData.total_revenue) * 100).toFixed(1)
                                            : '0.0';
                                        return (
                                            <tr key={dept.department} className="border-b border-white/5 hover:bg-white/5" data-testid={`dept-row-${idx}`}>
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 bg-amber-500/20 flex items-center justify-center rounded">
                                                            <Building className="w-4 h-4 text-amber-400" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium">{dept.department}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {dept.revenue_entries} rev, {dept.expense_entries} exp
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-sm text-right font-bold text-green-400">
                                                    {formatCurrency(dept.total_revenue)}
                                                </td>
                                                <td className="py-3 px-2 text-sm text-right font-bold text-red-400">
                                                    {formatCurrency(dept.total_expenses)}
                                                </td>
                                                <td className={`py-3 px-2 text-sm text-right font-bold ${dept.net_balance >= 0 ? 'text-cyan-400' : 'text-orange-400'}`}>
                                                    {formatCurrency(dept.net_balance)}
                                                </td>
                                                <td className="py-3 px-2 text-sm text-center text-muted-foreground">
                                                    {dept.revenue_entries + dept.expense_entries}
                                                </td>
                                                <td className="py-3 px-2 text-center">
                                                    <div className="flex items-center gap-2 justify-center">
                                                        <div className="w-16 h-2 bg-white/5 rounded-full overflow-hidden">
                                                            <div 
                                                                className="h-full bg-amber-400 rounded-full"
                                                                style={{ width: `${sharePercent}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-mono">{sharePercent}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Revenue Section */}
            {activeSection === 'revenue' && (
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-400" />
                            Revenue Entries ({revenues.length})
                        </h3>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Date</th>
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Category</th>
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Description</th>
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Payer</th>
                                    <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Amount</th>
                                    <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {revenues.length > 0 ? (
                                    revenues.map((revenue) => {
                                        const catInfo = getCategoryInfo(revenue.category, 'revenue');
                                        const Icon = catInfo.icon;
                                        
                                        return (
                                            <tr key={revenue.id} className="border-b border-white/5 hover:bg-white/5">
                                                <td className="py-3 px-2 text-sm">{revenue.date}</td>
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-6 h-6 ${catInfo.bgColor} flex items-center justify-center`}>
                                                            <Icon className={`w-3 h-3 ${catInfo.color}`} />
                                                        </div>
                                                        <span className="text-sm">{catInfo.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-sm">{revenue.description}</td>
                                                <td className="py-3 px-2 text-sm text-muted-foreground">{revenue.payer_name || '-'}</td>
                                                <td className="py-3 px-2 text-sm text-right font-bold text-green-400">
                                                    {formatCurrency(revenue.amount, revenue.currency)}
                                                </td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditRevenue(revenue)}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteRevenue(revenue.id)}
                                                            className="text-red-400 hover:text-red-300"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                                            No revenue entries found. Click "Add Revenue" to create one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Expenses Section */}
            {activeSection === 'expenses' && (
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-bold text-lg uppercase flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-red-400" />
                            Expense Entries ({expenses.length})
                        </h3>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Date</th>
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Category</th>
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Description</th>
                                    <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Payee</th>
                                    <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Amount</th>
                                    <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.length > 0 ? (
                                    expenses.map((expense) => {
                                        const catInfo = getCategoryInfo(expense.category, 'expense');
                                        const Icon = catInfo.icon;
                                        
                                        return (
                                            <tr key={expense.id} className="border-b border-white/5 hover:bg-white/5">
                                                <td className="py-3 px-2 text-sm">{expense.date}</td>
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-6 h-6 ${catInfo.bgColor} flex items-center justify-center`}>
                                                            <Icon className={`w-3 h-3 ${catInfo.color}`} />
                                                        </div>
                                                        <span className="text-sm">{catInfo.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-sm">{expense.description}</td>
                                                <td className="py-3 px-2 text-sm text-muted-foreground">{expense.payee_name || '-'}</td>
                                                <td className="py-3 px-2 text-sm text-right font-bold text-red-400">
                                                    {formatCurrency(expense.amount, expense.currency)}
                                                </td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditExpense(expense)}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteExpense(expense.id)}
                                                            className="text-red-400 hover:text-red-300"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                                            No expense entries found. Click "Add Expense" to create one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Financial Aid Report Section */}
            {activeSection === 'financial-aid' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border border-purple-500/30 p-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
                                    <Building className="w-5 h-5 text-purple-400" />
                                </div>
                                <span className="text-xs tracking-widest uppercase text-purple-400">Schools with Waiver</span>
                            </div>
                            <p className="stat-display text-3xl text-purple-400" data-testid="schools-with-waiver-count">
                                {schoolsWithWaiver.length}
                            </p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 border border-green-500/30 p-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                                    <Gift className="w-5 h-5 text-green-400" />
                                </div>
                                <span className="text-xs tracking-widest uppercase text-green-400">Full Waivers</span>
                            </div>
                            <p className="stat-display text-3xl text-green-400" data-testid="full-waiver-count">
                                {schoolsWithWaiver.filter(s => s.fee_waiver_type === 'full').length}
                            </p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 border border-amber-500/30 p-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
                                    <Receipt className="w-5 h-5 text-amber-400" />
                                </div>
                                <span className="text-xs tracking-widest uppercase text-amber-400">Partial Waivers</span>
                            </div>
                            <p className="stat-display text-3xl text-amber-400" data-testid="partial-waiver-count">
                                {schoolsWithWaiver.filter(s => s.fee_waiver_type === 'partial').length}
                            </p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/30 border border-cyan-500/30 p-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-cyan-500/20 flex items-center justify-center">
                                    <DollarSign className="w-5 h-5 text-cyan-400" />
                                </div>
                                <span className="text-xs tracking-widest uppercase text-cyan-400">Total Discount Value</span>
                            </div>
                            <p className="stat-display text-3xl text-cyan-400" data-testid="total-discount-value">
                                {formatCurrency(schoolsWithWaiver.reduce((sum, s) => sum + (s.fee_waiver_type === 'partial' ? (s.fee_waiver_amount || 0) : 0), 0))}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">Per registration (partial waivers)</p>
                        </div>
                    </div>
                    
                    {/* Schools with Waiver Table */}
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                            <Gift className="w-5 h-5 text-purple-400" />
                            Schools & Academies with Financial Aid
                        </h3>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">School/Academy</th>
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Type</th>
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Location</th>
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Sport</th>
                                        <th className="text-center py-3 px-2 text-xs text-muted-foreground uppercase">Waiver Type</th>
                                        <th className="text-right py-3 px-2 text-xs text-muted-foreground uppercase">Discount Amount</th>
                                        <th className="text-left py-3 px-2 text-xs text-muted-foreground uppercase">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {schoolsWithWaiver.length > 0 ? (
                                        schoolsWithWaiver.map((school) => (
                                            <tr key={school.id} className="border-b border-white/5 hover:bg-white/5">
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-8 h-8 ${school.fee_waiver_type === 'full' ? 'bg-green-500/20' : 'bg-amber-500/20'} flex items-center justify-center`}>
                                                            {school.fee_waiver_type === 'full' 
                                                                ? <Gift className="w-4 h-4 text-green-400" /> 
                                                                : <Receipt className="w-4 h-4 text-amber-400" />
                                                            }
                                                        </div>
                                                        <span className="font-medium">{school.school_name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-sm text-muted-foreground">
                                                    {school.institution_type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Sports Academy'}
                                                </td>
                                                <td className="py-3 px-2 text-sm text-muted-foreground">{school.city}, {school.country}</td>
                                                <td className="py-3 px-2 text-sm">{school.sport}</td>
                                                <td className="py-3 px-2 text-center">
                                                    {school.fee_waiver_type === 'full' ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                            <Gift className="w-3 h-3" />
                                                            100% Free
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                                            <Receipt className="w-3 h-3" />
                                                            Partial
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2 text-sm text-right font-bold">
                                                    {school.fee_waiver_type === 'full' ? (
                                                        <span className="text-green-400">Full Waiver</span>
                                                    ) : (
                                                        <span className="text-amber-400">{formatCurrency(school.fee_waiver_amount || 0)} off</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2 text-sm text-muted-foreground">
                                                    {school.fee_waiver_reason || '-'}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="py-8 text-center text-muted-foreground">
                                                No schools with fee waivers found. Add waivers to schools in the "Schools" tab.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    {/* Waiver by Type Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-[#0d2847] border border-green-500/30 p-6">
                            <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                                <Gift className="w-5 h-5 text-green-400" />
                                Full Waiver Schools
                            </h3>
                            <div className="space-y-2">
                                {schoolsWithWaiver.filter(s => s.fee_waiver_type === 'full').length > 0 ? (
                                    schoolsWithWaiver.filter(s => s.fee_waiver_type === 'full').map(school => (
                                        <div key={school.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                            <div>
                                                <p className="text-sm font-medium">{school.school_name}</p>
                                                <p className="text-xs text-muted-foreground">{school.sport} • {school.city}</p>
                                            </div>
                                            <span className="text-green-400 text-sm font-bold">100% FREE</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">No full waiver schools</p>
                                )}
                            </div>
                        </div>
                        
                        <div className="bg-[#0d2847] border border-amber-500/30 p-6">
                            <h3 className="font-heading font-bold text-lg uppercase mb-4 flex items-center gap-2">
                                <Receipt className="w-5 h-5 text-amber-400" />
                                Partial Waiver Schools
                            </h3>
                            <div className="space-y-2">
                                {schoolsWithWaiver.filter(s => s.fee_waiver_type === 'partial').length > 0 ? (
                                    schoolsWithWaiver.filter(s => s.fee_waiver_type === 'partial').map(school => (
                                        <div key={school.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                            <div>
                                                <p className="text-sm font-medium">{school.school_name}</p>
                                                <p className="text-xs text-muted-foreground">{school.sport} • {school.city}</p>
                                            </div>
                                            <span className="text-amber-400 text-sm font-bold">-{formatCurrency(school.fee_waiver_amount || 0)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">No partial waiver schools</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Revenue Dialog */}
            <Dialog open={showRevenueDialog} onOpenChange={setShowRevenueDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-400" />
                            {editingRevenue ? 'Edit Revenue' : 'Add Revenue'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Category *</Label>
                                <Select value={revenueForm.category || undefined} onValueChange={(v) => setRevenueForm({...revenueForm, category: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {REVENUE_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Date</Label>
                                <Input 
                                    type="date" 
                                    value={revenueForm.date} 
                                    onChange={(e) => setRevenueForm({...revenueForm, date: e.target.value})}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Description *</Label>
                            <Input 
                                value={revenueForm.description} 
                                onChange={(e) => setRevenueForm({...revenueForm, description: e.target.value})}
                                placeholder="Enter description"
                                className="bg-[#0a1628] border-white/10"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Amount *</Label>
                                <Input 
                                    type="number" 
                                    value={revenueForm.amount} 
                                    onChange={(e) => setRevenueForm({...revenueForm, amount: e.target.value})}
                                    placeholder="0"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Currency</Label>
                                <Select value={revenueForm.currency} onValueChange={(v) => setRevenueForm({...revenueForm, currency: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="KES">KES</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Tournament (Optional)</Label>
                                <Select value={revenueForm.tournament_id || "none"} onValueChange={(v) => setRevenueForm({...revenueForm, tournament_id: v === "none" ? "" : v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select tournament" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="none">None</SelectItem>
                                        {tournaments?.map(t => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Payment Method</Label>
                                <Select value={revenueForm.payment_method || "none"} onValueChange={(v) => setRevenueForm({...revenueForm, payment_method: v === "none" ? "" : v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select method" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="none">Select method</SelectItem>
                                        {PAYMENT_METHODS.map(m => (
                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Payer Name</Label>
                                <Input 
                                    value={revenueForm.payer_name} 
                                    onChange={(e) => setRevenueForm({...revenueForm, payer_name: e.target.value})}
                                    placeholder="Who paid?"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Reference Number</Label>
                                <Input 
                                    value={revenueForm.reference_number} 
                                    onChange={(e) => setRevenueForm({...revenueForm, reference_number: e.target.value})}
                                    placeholder="Receipt/Invoice #"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Notes</Label>
                            <Textarea 
                                value={revenueForm.notes} 
                                onChange={(e) => setRevenueForm({...revenueForm, notes: e.target.value})}
                                placeholder="Additional notes..."
                                className="bg-[#0a1628] border-white/10"
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRevenueDialog(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveRevenue} className="bg-green-600 hover:bg-green-700">
                            {editingRevenue ? 'Update' : 'Save'} Revenue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Expense Dialog */}
            <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-red-400" />
                            {editingExpense ? 'Edit Expense' : 'Add Expense'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Category *</Label>
                                <Select value={expenseForm.category || undefined} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10 max-h-[200px]">
                                        {EXPENSE_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Date</Label>
                                <Input 
                                    type="date" 
                                    value={expenseForm.date} 
                                    onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Description *</Label>
                            <Input 
                                value={expenseForm.description} 
                                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                                placeholder="Enter description"
                                className="bg-[#0a1628] border-white/10"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Amount *</Label>
                                <Input 
                                    type="number" 
                                    value={expenseForm.amount} 
                                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                                    placeholder="0"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Currency</Label>
                                <Select value={expenseForm.currency} onValueChange={(v) => setExpenseForm({...expenseForm, currency: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="KES">KES</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Tournament (Optional)</Label>
                                <Select value={expenseForm.tournament_id || "none"} onValueChange={(v) => setExpenseForm({...expenseForm, tournament_id: v === "none" ? "" : v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select tournament" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="none">None</SelectItem>
                                        {tournaments?.map(t => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Payment Method</Label>
                                <Select value={expenseForm.payment_method || "none"} onValueChange={(v) => setExpenseForm({...expenseForm, payment_method: v === "none" ? "" : v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select method" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="none">Select method</SelectItem>
                                        {PAYMENT_METHODS.map(m => (
                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Payee Name</Label>
                                <Input 
                                    value={expenseForm.payee_name} 
                                    onChange={(e) => setExpenseForm({...expenseForm, payee_name: e.target.value})}
                                    placeholder="Who received payment?"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Reference Number</Label>
                                <Input 
                                    value={expenseForm.reference_number} 
                                    onChange={(e) => setExpenseForm({...expenseForm, reference_number: e.target.value})}
                                    placeholder="Receipt/Invoice #"
                                    className="bg-[#0a1628] border-white/10"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Notes</Label>
                            <Textarea 
                                value={expenseForm.notes} 
                                onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                                placeholder="Additional notes..."
                                className="bg-[#0a1628] border-white/10"
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExpenseDialog(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveExpense} className="bg-red-600 hover:bg-red-700">
                            {editingExpense ? 'Update' : 'Save'} Expense
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Templates Dialog */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-cyan-400" />
                            Import Templates
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Download template files to ensure your data is formatted correctly for import.
                        </p>
                        
                        {/* Revenue Templates */}
                        <div className="bg-green-900/20 border border-green-500/30 p-4 space-y-3">
                            <h4 className="font-semibold text-green-400 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Revenue Template
                            </h4>
                            <p className="text-xs text-muted-foreground">
                                Required columns: category, description, amount
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadTemplate('revenue', 'csv')}
                                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                                >
                                    <Download className="w-4 h-4 mr-1" />
                                    CSV
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadTemplate('revenue', 'excel')}
                                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                                >
                                    <Download className="w-4 h-4 mr-1" />
                                    Excel
                                </Button>
                            </div>
                        </div>

                        {/* Expense Templates */}
                        <div className="bg-red-900/20 border border-red-500/30 p-4 space-y-3">
                            <h4 className="font-semibold text-red-400 flex items-center gap-2">
                                <TrendingDown className="w-4 h-4" />
                                Expenses Template
                            </h4>
                            <p className="text-xs text-muted-foreground">
                                Required columns: category, description, amount
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadTemplate('expenses', 'csv')}
                                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                    <Download className="w-4 h-4 mr-1" />
                                    CSV
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadTemplate('expenses', 'excel')}
                                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                    <Download className="w-4 h-4 mr-1" />
                                    Excel
                                </Button>
                            </div>
                        </div>

                        <div className="bg-[#0a1628] p-3 text-xs text-muted-foreground">
                            <p className="font-semibold mb-2">Valid Categories:</p>
                            <p><span className="text-green-400">Revenue:</span> Registration Fee, Sponsorship Revenue, Branding Revenue, Stalls Renting, Vouchers and Gifts, Others</p>
                            <p className="mt-2"><span className="text-red-400">Expenses:</span> Officials/Referees/Volunteers, Vendors, Venue Rent, Medical Team, Security, Medals, Trophies, Prize Money, Food Expenses, Transport Expenses, Others</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowImportDialog(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AccountingTab;
